<template>
	<el-dialog title="配置首页布局" :visible.sync="visible" width="96%" top="3vh" append-to-body :close-on-click-modal="false" :before-close="beforeClose" @closed="stopResize">
		<div v-loading="loading" :class="{saving}">
			<div class="editor-toolbar">
				<el-input v-model="form.name" placeholder="模板名称" maxlength="50" class="meta-input" :disabled="!editable || saving" />
				<el-input v-model="form.code" placeholder="模板编码" maxlength="50" class="meta-input" :disabled="!editable || saving" />
				<el-input v-model="form.desc" placeholder="模板描述（选填）" maxlength="500" class="description" :disabled="!editable || saving" />
				<el-button @click="preview">预览</el-button>
				<el-button v-if="editable" :loading="saving" @click="save">保存草稿</el-button>
				<el-button v-if="canPublish" type="primary" :loading="saving" :disabled="dirty || !form.keyId" @click="publish">发布</el-button>
			</div>
			<div class="layout-editor">
				<aside class="widget-library">
					<h4>可选统计内容</h4>
					<p class="hint">拖入画布添加组件；数字指标可直接拖入统计块。</p>
					<el-button v-if="editable" class="add-number-group" type="primary" plain icon="el-icon-plus" @click="addGroup">添加数字统计块</el-button>
					<lcsj-filter :show-search="false"><el-form-item label="统计内容："><el-input v-model="search" placeholder="名称" size="small" clearable /></el-form-item></lcsj-filter>
					<div v-for="group in groups" :key="group.name">
						<h5>{{ group.name }}</h5>
						<div v-for="w in group.widgets" :key="w.keyId" class="library-widget" :draggable="editable && !saving" @dragstart="beginDrag($event,{widget:w})" @dragend="endDrag">
							<span>
								<i class="el-icon-rank" />
								{{ w.name }}
							</span>
							<el-button v-if="editable" type="text" icon="el-icon-plus" @click="add(w)" />
						</div>
					</div>
					<el-empty v-if="!groups.length" description="暂无可用统计内容" :image-size="55" />
				</aside>
				<main class="layout-canvas">
					<div class="canvas-heading">
						<strong>首页布局</strong>
						<el-tag size="small" type="info">示例数据</el-tag>
					</div>
					<h3>欢迎您，用户</h3>
					<div v-for="row in rows" :key="row" class="canvas-row" @dragover.prevent @drop.prevent="drop($event, row)">
						<div
							v-for="entry in rowItems(row)"
							:key="entry.index"
							class="canvas-item"
							:class="{ selected: selected === entry.index, 'group-drop-active': groupHover === entry.index }"
							:style="{ gridColumn: entry.item.col + '/span ' + entry.item.width }"
							:draggable="editable"
							@dragstart.stop="beginDrag($event,{index:entry.index})"
							@dragend="endDrag"
							@dragover="overGroup($event, entry.index)"
							@dragleave="leaveGroup($event, entry.index)"
							@drop="dropGroup($event, entry.index)"
							@click="selected = entry.index"
						>
							<home-widget :placement="entry.item" :widget="widget(entry.item)" :value="example(widget(entry.item))" :children="previewChildren(entry.item)" empty-group-text="从左侧拖入数字指标" />
							<div v-if="entry.item.groupKey && editable" class="group-drop-hint">{{ groupHover === entry.index ? '松开鼠标，添加到此统计块' : '可继续从左侧拖入数字指标' }}</div>
							<span v-if="editable" class="resize-handle" @mousedown.prevent.stop="startResize($event, entry.index)" title="拖动调整宽度" />
						</div>
						<span v-if="!rowItems(row).length" class="drop-placeholder">拖入统计内容，添加到第 {{ row }} 行</span>
					</div>
				</main>
				<aside class="properties">
					<h4>属性设置</h4>
					<el-form v-if="current" label-position="top" size="small" :disabled="!editable || saving">
						<el-form-item label="统计内容">
							<strong>{{ widget(current).name }}</strong>
						</el-form-item>
						<el-form-item label="显示标题"><el-input v-model="current.title" maxlength="50" /></el-form-item>
						<template v-if="current.groupKey">
							<p class="hint">从左侧将数字指标拖入统计块，在此调整已添加指标的属性。</p>
							<div v-for="(child, index) in current.children" :key="child.widgetKeyId" class="number-properties">
								<strong>{{ widget(child).name }}</strong>
								<el-form-item label="指标标题"><el-input v-model="child.title" maxlength="50" /></el-form-item>
								<el-form-item label="时间范围"><el-select v-model="child.range"><el-option v-for="r in metric(child).ranges" :key="r" :label="rangeNames[r]" :value="r" /></el-select></el-form-item>
								<el-form-item label="跳转业务列表"><el-switch v-model="child.link" /></el-form-item>
								<el-button type="text" :disabled="index === 0" @click="moveNumber(index, -1)">上移</el-button>
								<el-button type="text" :disabled="index === current.children.length - 1" @click="moveNumber(index, 1)">下移</el-button>
								<el-button type="text" @click="removeNumber(index)">移除指标</el-button>
							</div>
						</template>
						<el-form-item v-else label="时间范围">
							<el-select v-model="current.range"><el-option v-for="r in metric(current).ranges" :key="r" :label="rangeNames[r]" :value="r" /></el-select>
						</el-form-item>
						<el-form-item label="所在行 / 列">
							<el-input-number v-model="current.row" :min="1" :max="100" controls-position="right" />
							<el-input-number v-model="current.col" :min="1" :max="12" controls-position="right" />
						</el-form-item>
						<el-form-item label="占用宽度（12列栅格）"><el-slider v-model="current.width" :min="1" :max="12" show-stops /></el-form-item>
						<el-form-item label="高度（像素）"><el-input-number v-model="current.height" :min="100" :max="600" :step="20" /></el-form-item>
						<el-form-item v-if="!current.groupKey" label="跳转业务列表">
							<el-switch v-model="current.link" />
							<p class="hint">打开当前用户有权访问的对应业务列表。</p>
						</el-form-item>
						<el-button type="danger" plain @click="remove">移除组件</el-button>
					</el-form>
					<el-empty v-else description="选择组件调整属性" :image-size="60" />
				</aside>
			</div>
			<el-alert v-if="error" :title="error" type="error" :closable="false" />
			<div class="editor-footer">
				{{ dirty ? "有未保存修改" : "草稿已保存" }} · 保存草稿不影响首页，发布后对关联角色生效。
				<span v-if="roles.length">关联角色：{{ roles.join("、") }}</span>
			</div>
		</div>
		<home-preview ref="preview" />
	</el-dialog>
</template>
<script>
import { homePageRequest as api } from "@/api/homePageApi";
import { rangeNames, layoutError, nextPosition, exampleValue, flattenLayout, editorLayout } from "@/utils/homeLayout";
import HomeWidget from "@/views/mainIndex/homeWidget.vue";
import HomePreview from "./homePreview.vue";
export default {
	components: { HomeWidget, HomePreview },
	props: { permissions: Array },
	data: () => ({
		visible: false,
		loading: false,
		saving: false,
		form: {},
		items: [],
		widgets: [],
		metrics: [],
		selected: -1,
		search: "",
		drag: null,
		groupHover: -1,
		baseline: "",
		roles: [],
		snapshots: {},
		rangeNames
	}),
	computed: {
		editable() {
			return this.permissions.includes("homeTemplateEdit");
		},
		canPublish() {
			return this.permissions.includes("homeTemplatePublish");
		},
		current() {
			return this.items[this.selected];
		},
		error() {
			return layoutError(this.items);
		},
		dirty() {
			return this.baseline !== this.serialize();
		},
		groups() {
			return [...new Set(this.metrics.map((m) => m.category))]
				.map((name) => ({ name, widgets: this.widgets.filter((w) => w.enabled && this.metrics.find((m) => m.code === w.metric && m.category === name) && w.name.includes(this.search)) }))
				.filter((g) => g.widgets.length);
		},
		rows() {
			const max = Math.min(100, Math.max(1, ...this.items.map((i) => i.row || 1)) + 1);
			return Array.from({ length: max }, (_, i) => i + 1);
		}
	},
	beforeDestroy() {
		this.stopResize();
	},
	methods: {
		beginDrag(event, payload) { if(this.saving || !this.editable) { event.preventDefault(); return; } this.drag=payload; event.dataTransfer.effectAllowed=payload.widget ? 'copyMove' : 'move'; event.dataTransfer.setData('text/plain', 'home-widget'); },
		endDrag() { this.drag = null; this.groupHover = -1; },
		overGroup(event, index) {
			if (!this.editable || this.saving || !this.items[index].groupKey || !this.drag || !this.drag.widget) return;
			event.preventDefault(); event.stopPropagation();
			const allowed = this.drag.widget.displayType === 'number';
			event.dataTransfer.dropEffect = allowed ? 'copy' : 'none';
			this.groupHover = allowed ? index : -1;
		},
		leaveGroup(event, index) {
			if (this.groupHover === index && !event.currentTarget.contains(event.relatedTarget)) this.groupHover = -1;
		},
		dropGroup(event, index) {
			const group = this.items[index];
			if (!this.editable || this.saving || !group.groupKey || !this.drag || !this.drag.widget) return;
			event.preventDefault(); event.stopPropagation();
			const w = this.drag.widget;
			this.endDrag();
			if (w.displayType !== 'number') return this.$message.warning('统计块只能放入数字指标，图表和列表请拖入画布');
			if (group.children.some(c => c.widgetKeyId === w.keyId)) return this.$message.warning('此统计块已包含该指标');
			if (flattenLayout(this.items).length >= 50) return this.$message.warning('每个模板最多50个统计指标');
			group.children.push({ widgetKeyId: w.keyId, title: w.name, range: w.range, link: false });
			this.selected = index;
		},
		example: exampleValue,
		serialize() {
			return JSON.stringify({ form: this.form, items: this.items });
		},
		widget(p) {
			if (p.groupKey && p.children) return { name: '数字统计块', displayType: 'group' };
			return this.widgets.find((w) => w.keyId === p.widgetKeyId) || this.snapshots[p.widgetKeyId] || { name: "已删除统计内容", displayType: "number", decimals: 0, color: "#0e7c66" };
		},
		metric(p) {
			return this.metrics.find((m) => m.code === this.widget(p).metric) || { ranges: [] };
		},
		rowItems(row) {
			return this.items.map((item, index) => ({ item, index })).filter((e) => e.item.row === row);
		},
		async open(row, copy = false) {
			this.visible = true;
			this.loading = true;
			this.selected = -1;
			this.endDrag();
			this.search = "";
			this.roles = row ? row.roles || [] : [];
			this.snapshots = {};
			try {
				const [widgets, catalog, detail] = await Promise.all([api("Widgets"), api("Catalog"), row ? api("Detail", { keyId: row.template.keyId }) : Promise.resolve(null)]);
				this.widgets = widgets;
				this.metrics = catalog.metrics;
				this.form = detail
					? { keyId: detail.template.keyId, revision: detail.template.revision, name: detail.template.name, code: detail.template.code, desc: detail.template.desc }
					: { name: "", code: "", desc: "", revision: 0 };
				if (detail) detail.items.forEach(x => { this.snapshots[x.placement.widgetKeyId] = x.widget; });
				this.items = detail ? editorLayout(detail.items.map(x => ({ ...x, placement: { widgetKeyId: x.placement.widgetKeyId, title: x.placement.title, range: x.placement.range, row: x.placement.row, col: x.placement.col, width: x.placement.width, height: x.placement.height, link: x.placement.link, groupKey: x.placement.groupKey, groupTitle: x.placement.groupTitle, groupOrder: x.placement.groupOrder } }))) : [];
				if (copy) {
					this.form.keyId = null;
					this.form.revision = 0;
					this.form.name += "（副本）";
					this.form.code = "";
					this.roles = [];
				}
				this.baseline = this.serialize();
			} catch (e) {
				this.$message.error(e.message);
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},
		add(w) {
			if (!this.editable || this.saving) return;
			const width = w.displayType === "number" ? 3 : 12,
				pos = nextPosition(this.items, width);
			if (!pos || this.items.length >= 50) return this.$message.warning("每个模板最多50个组件");
			this.items.push({ widgetKeyId: w.keyId, title: w.name, range: w.range, ...pos, width, height: w.displayType === "number" ? 140 : 320, link: false });
			this.selected = this.items.length - 1;
		},
		addGroup() {
			if (!this.editable || this.saving) return;
			const pos = nextPosition(this.items, 12);
			if (!pos) return this.$message.warning('请先完成当前统计块配置，模板最多50个指标');
			this.items.push({ groupKey: 'g_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 9), title: '数据概览', ...pos, width: 12, height: 200, children: [] });
			this.selected = this.items.length - 1;
		},
		removeNumber(index) { if (this.editable && !this.saving) this.current.children.splice(index, 1); },
		moveNumber(index, direction) { const children = this.current.children; const moved = children.splice(index, 1)[0]; children.splice(index + direction, 0, moved); },
		previewChildren(p) { return p.children ? p.children.map((c, groupOrder) => ({ placement: { ...c, groupOrder }, widget: this.widget(c), value: exampleValue(this.widget(c)) })) : undefined; },
		drop(event, row) {
			if (!this.editable || this.saving || !this.drag) return;
			const rect = event.currentTarget.getBoundingClientRect();
			const col = Math.min(12, Math.max(1, Math.floor(((event.clientX - rect.left) / rect.width) * 12) + 1));
			if (this.drag.widget) {
				if (this.items.length >= 50) { this.drag = null; return this.$message.warning("每个模板最多50个组件"); }
				this.add(this.drag.widget);
				if (this.current) {
					this.current.row = row;
					this.current.col = Math.min(col, 13 - this.current.width);
				}
			} else {
				this.selected = this.drag.index;
				this.current.row = row;
				this.current.col = Math.min(col, 13 - this.current.width);
			}
			this.drag = null;
		},
		remove() {
			this.items.splice(this.selected, 1);
			this.selected = -1;
		},
		startResize(event, index) {
			if (this.saving || !this.editable) return;
			this.selected = index;
			const start = event.clientX,
				width = this.current.width,
				cell = event.currentTarget.closest(".canvas-row").getBoundingClientRect().width / 12;
			this.moveResize = (e) => {
				this.current.width = Math.max(1, Math.min(13 - this.current.col, width + Math.round((e.clientX - start) / cell)));
			};
			document.addEventListener("mousemove", this.moveResize);
			document.addEventListener("mouseup", this.stopResize);
		},
		stopResize() {
			if (this.moveResize) document.removeEventListener("mousemove", this.moveResize);
			document.removeEventListener("mouseup", this.stopResize);
		},
		preview() {
			this.$refs.preview.open(
				this.form.name || "新模板",
				this.items.map((p) => ({ placement: p, widget: this.widget(p), children: this.previewChildren(p) }))
			);
		},
		async save() {
			if (this.saving || !this.editable) return;
			if (this.error) return this.$message.error(this.error);
			this.saving = true;
			try {
				const t = await api("SaveTemplate", { ...this.form, items: flattenLayout(this.items) });
				this.form = { ...this.form, keyId: t.keyId, revision: t.revision };
				this.baseline = this.serialize();
				this.$message.success("草稿已保存");
				this.$emit("saved");
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.saving = false;
			}
		},
		async publish() {
			if (this.saving || this.dirty || !this.canPublish) return;
			try {
				await this.$confirm("发布后将更新关联角色的首页展示，是否继续？", "发布模板");
			} catch (_) {
				return;
			}
			this.saving = true;
			try {
				const t = await api("TemplateAction", { keyId: this.form.keyId, revision: this.form.revision, action: "publish" });
				this.form.revision = t.revision;
				this.baseline = this.serialize();
				this.$emit("saved");
				this.$message.success("模板已发布");
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.saving = false;
			}
		},
		beforeClose(done) {
			if (this.saving) return;
			if (!this.dirty) return done();
			this.$confirm("有未保存修改，确定关闭？", "提示")
				.then(() => done())
				.catch(() => {});
		}
	}
};
</script>
<style scoped>
.add-number-group { width: 100%; margin-bottom: 12px; }
.number-properties { padding: 12px; margin-bottom: 12px; background: var(--app-surface-muted); border-radius: 6px; }
.saving .layout-editor { pointer-events: none; }
.editor-toolbar {
	display: flex;
	gap: 10px;
	margin-bottom: 16px;
	align-items: center;
}
.meta-input {
	width: 170px;
}
.description {
	flex: 1;
}
.layout-editor {
	display: grid;
	grid-template-columns: 210px minmax(0, 1fr) 250px;
	height: 65vh;
	border: 1px solid var(--app-border);
}
.widget-library,
.properties {
	padding: 16px;
	overflow: auto;
	background: var(--app-surface);
}
.widget-library {
	border-right: 1px solid var(--app-border);
}
.properties {
	border-left: 1px solid var(--app-border);
}
h4 {
	margin: 0 0 16px;
}
h5 {
	margin: 18px 0 8px;
}
.library-widget {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border: 1px solid var(--app-border);
	border-radius: 5px;
	padding: 2px 8px;
	margin-bottom: 8px;
	cursor: grab;
	font-size: 12px;
}
.layout-canvas {
	overflow: auto;
	padding: 16px;
	background: var(--app-surface-muted);
	background-image: radial-gradient(var(--app-border) 1px, transparent 1px);
	background-size: 12px 12px;
}
.canvas-heading {
	display: flex;
	justify-content: space-between;
}
.canvas-row {
	display: grid;
	grid-template-columns: repeat(12, minmax(0, 1fr));
	gap: 10px;
	margin-bottom: 12px;
	min-height: 70px;
}
.canvas-item {
	position: relative;
	cursor: move;
	border: 2px solid transparent;
	border-radius: 8px;
	min-width: 0;
}
.canvas-item.selected {
	border-color: var(--app-primary);
}
.canvas-item.group-drop-active { border-color: var(--app-primary); outline: 2px dashed var(--app-primary); outline-offset: 3px; }
.group-drop-hint { padding: 8px; text-align: center; font-size: 12px; color: var(--app-primary); background: var(--app-surface); border-top: 1px dashed var(--app-border); }
.resize-handle {
	position: absolute;
	bottom: 0;
	right: 0;
	width: 13px;
	height: 13px;
	background: var(--app-primary);
	cursor: ew-resize;
}
.drop-placeholder {
	grid-column: span 12;
	text-align: center;
	padding: 24px;
	border: 1px dashed var(--app-border-strong);
	color: var(--app-text-secondary);
}
.properties .el-input-number {
	max-width: 100%;
	width: 105px;
}
.hint,
.editor-footer {
	font-size: 12px;
	color: var(--app-text-secondary);
}
.editor-footer {
	padding-top: 14px;
}
.editor-footer span {
	margin-left: 20px;
}
.properties .el-select {
	width: 100%;
}
@media (max-width: 1100px) {
	.layout-editor {
		grid-template-columns: 170px minmax(0, 1fr) 210px;
	}
	.editor-toolbar {
		flex-wrap: wrap;
	}
}
</style>
